import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as workspaceApi from "../services/workspaceService";
import { getActiveWorkspaceId, setActiveWorkspaceId } from "../services/workspaceSession";

function readItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.workspaces)) return payload.workspaces;
  return [];
}

export const fetchWorkspacesAsync = createAsyncThunk("workspace/fetch", async () => workspaceApi.getWorkspaces());
export const createWorkspaceAsync = createAsyncThunk("workspace/create", async (body) => workspaceApi.createWorkspace(body));
export const archiveWorkspaceAsync = createAsyncThunk("workspace/archive", async (workspaceId) => {
  await workspaceApi.archiveWorkspace(workspaceId);
  return workspaceId;
});

const initialState = {
  items: [],
  activeWorkspaceId: getActiveWorkspaceId(),
  loading: false,
  error: null,
  loaded: false,
  defaultWorkspaceId: null,
  used: 0,
  limit: null,
};

const slice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    switchWorkspace(state, action) {
      const workspaceId = String(action.payload || "").trim() || null;
      setActiveWorkspaceId(workspaceId);
      state.activeWorkspaceId = workspaceId;
    },
    clearWorkspaceError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspacesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkspacesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.items = readItems(action.payload).filter((item) => item?.status !== "archived");
        state.defaultWorkspaceId = action.payload?.default_workspace_id || action.payload?.defaultWorkspaceId || null;
        state.used = Number(action.payload?.used ?? state.items.filter((item) => !item?.is_default).length);
        const responseLimit = action.payload?.limit;
        state.limit = responseLimit === undefined || responseLimit === null ? state.limit : Number(responseLimit);
        const selectedExists = state.items.some((item) => item.workspace_id === state.activeWorkspaceId);
        if (!selectedExists) {
          const preferred = state.items.find((item) => item?.is_default) || state.items[0] || null;
          state.activeWorkspaceId = preferred?.workspace_id || null;
          setActiveWorkspaceId(state.activeWorkspaceId);
        }
      })
      .addCase(fetchWorkspacesAsync.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.error?.message || "Unable to load client workspaces.";
      })
      .addCase(createWorkspaceAsync.fulfilled, (state, action) => {
        const item = action.payload?.workspace || action.payload;
        if (item?.workspace_id && !state.items.some((existing) => existing.workspace_id === item.workspace_id)) {
          state.items.push(item);
        }
        state.used = Number(action.payload?.used ?? state.items.filter((workspace) => !workspace?.is_default).length);
        if (action.payload?.limit !== undefined && action.payload?.limit !== null) {
          state.limit = Number(action.payload.limit);
        }
      })
      .addCase(archiveWorkspaceAsync.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.workspace_id !== action.payload);
        state.used = state.items.filter((workspace) => !workspace?.is_default).length;
        if (state.activeWorkspaceId === action.payload) {
          state.activeWorkspaceId = state.items[0]?.workspace_id || null;
          setActiveWorkspaceId(state.activeWorkspaceId);
        }
      });
  },
});

export const { switchWorkspace, clearWorkspaceError } = slice.actions;
export const selectWorkspaces = (state) => state.workspace?.items || [];
export const selectActiveWorkspaceId = (state) => state.workspace?.activeWorkspaceId || null;
export const selectActiveWorkspace = (state) =>
  selectWorkspaces(state).find((item) => item.workspace_id === selectActiveWorkspaceId(state)) || null;
export const selectWorkspaceUsed = (state) => Number(state.workspace?.used ?? 0);
export const selectWorkspaceLimit = (state) => state.workspace?.limit;
export default slice.reducer;
